import React, { useState } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Divider, 
  TextField, 
  Button as MuiButton,
  Alert
} from '@mui/material';
import { 
  ThumbDown, 
  Feedback as FeedbackIcon, 
  Replay as ReprocessIcon 
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { FeedbackSectionProps } from '../types';
import LoadLatestResultsButton from '@/components/LoadLatestResultsButton';
import { useToast } from '@/context/ToastContext';
import { useVerifyAnalysis } from '@/services/module1Service';

const FeedbackSection: React.FC<FeedbackSectionProps> = ({ 
  workflowId, 
  onApproved, 
  onReprocess, 
  isProcessing,
  serviceAvailable = true,
  onResultsLoaded
}) => {
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const { showToast } = useToast();
  const { clientId } = useParams();
  const verifyMutation = useVerifyAnalysis();

  // Handle approve action
  const handleApprove = async () => {
    if (!workflowId) {
      showToast('No workflow ID available', 'error');
      return;
    }
    
    try {
      await verifyMutation.mutateAsync({
        workflow_id: workflowId,
        action: 'finalize',
        feedback: feedback || 'Results approved',
        client_id: clientId
      });
      
      showToast('Analysis results approved and finalized', 'success');
      setFeedback('');
      setShowFeedback(false);
      
      // Call onApproved callback if provided
      if (onApproved) {
        onApproved();
      }
    } catch (error) {
      console.error('Error approving analysis:', error);
      showToast('Failed to approve analysis', 'error');
    }
  };
  
  // Handle reject action
  const handleReject = async () => {
    if (!workflowId) {
      showToast('No workflow ID available', 'error');
      return;
    }
    
    if (!feedback.trim()) {
      showToast('Please provide feedback when rejecting analysis', 'error');
      return;
    }
    
    try {
      await verifyMutation.mutateAsync({
        workflow_id: workflowId,
        action: 'reject',
        feedback: feedback,
        client_id: clientId
      });
      
      showToast('Analysis results rejected with feedback', 'info');
      setFeedback('');
      setShowFeedback(false);
    } catch (error) {
      console.error('Error rejecting analysis:', error);
      showToast('Failed to reject analysis', 'error');
    }
  };

  return (
    <Card sx={{ mt: 4 }}>
      <CardContent>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 2 
        }}>            
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {/* Reprocess Analysis button */}
            {onReprocess && (
              <MuiButton 
                variant="outlined" 
                color="primary"
                onClick={onReprocess}
                startIcon={<ReprocessIcon />}
                disabled={isProcessing || !serviceAvailable}
              >
                {isProcessing ? 'Processing...' : 'Reprocess Analysis'}
              </MuiButton>
            )}

            {/* Load Latest Results button */}
            {onResultsLoaded && (
              <LoadLatestResultsButton 
                onResultsLoaded={onResultsLoaded}
                disabled={isProcessing || !serviceAvailable}
                clientId={clientId}
                workflowId={workflowId}
              />
            )}

            {!showFeedback ? (
              <MuiButton 
                variant="outlined" 
                color="primary"
                onClick={() => setShowFeedback(true)}
                startIcon={<FeedbackIcon />}
              >
                Add Feedback
              </MuiButton>
            ) : (
              <MuiButton 
                variant="outlined" 
                color="inherit"
                onClick={() => setShowFeedback(false)}
              >
                Cancel
              </MuiButton>
            )}                
          </Box>
        </Box>
        
        {/* Service unavailability warning */}
        {!serviceAvailable && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Module 1 service is currently unavailable. Reprocessing and new analyses are disabled.
          </Alert>
        )}
        
        <Divider />
        
        {showFeedback && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Provide Feedback or Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter your feedback or notes about this analysis..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <MuiButton
                variant="contained"
                color="error"
                startIcon={<ThumbDown />}
                onClick={handleReject}
                disabled={verifyMutation.isPending || !feedback.trim()}
              >
                Reject Analysis
              </MuiButton>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FeedbackSection;
